import { useState, useCallback } from 'react';
import { API_CONFIG, sortCategoriesWithConfig, isLockedProduct, getDemoData, isFlightSimulator, sanitizeInput, validateUrlParam } from '../AppUtils.js';

// Hook for managing product data - Enhanced with access control and security
export const useProductData = () => {
  const [productData, setProductData] = useState({});
  const [mainCategories, setMainCategories] = useState([]);
  const [categoryConfig, setCategoryConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams(window.location.search);
      const rawSlug = params.get('slug');
      
      // Validate and sanitize slug
      const slug = rawSlug ? validateUrlParam(rawSlug, 'slug') : null;
      
      // For non-admin access, slug is required and must be validated by AccessControl first
      if (!slug || (slug.toLowerCase() !== 'admin' && !slug.trim())) {
        throw new Error('No valid slug provided');
      }
      
      const fetchUrl = slug && slug.toLowerCase() !== 'admin' 
        ? `${API_CONFIG.baseUrl}&slug=${encodeURIComponent(slug)}` 
        : API_CONFIG.baseUrl;
      
      const response = await fetch(fetchUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // STEP 1: USE THE BACKEND'S CATEGORYCONFIG DIRECTLY!
      const backendCategoryConfig = data.categoryConfig || {};
      
      // STEP 2: Create enhanced lookup maps that PRESERVE all CMS data
      const categoryLookup = {};
      const subcategoryLookup = {};
      
      if (data.masterCategories) {
        data.masterCategories.forEach(cat => {
          categoryLookup[cat._id] = {
            name: sanitizeInput(cat.categoryName),
            order: cat.categoryOrder,
            icon: cat.icon,
            _id: cat._id
          };
        });
      }
      
      if (data.masterSubcategories) {
        data.masterSubcategories.forEach(sub => {
          subcategoryLookup[sub._id] = {
            name: sanitizeInput(sub.subCategoryName),
            order: sub.subCategoryOrder,
            parentCategory: sub.parentCategory,
            icon: sub.icon,
            _id: sub._id
          };
        });
      }
      
      // STEP 3: Group products using EXACT CMS names from backend config
      const groupedProducts = {};
      const items = data.items || [];

      // Get current base simulator ID for showOnlyWith filtering
      const currentSimulatorId = data.baseConfig?.id || null;

      items.forEach(item => {
        // FILTER: Check showOnlyWith field (comes as array of simulator IDs)
        // If a module has showOnlyWith set, only include it if the current simulator is in that list
        if (item.showOnlyWith && Array.isArray(item.showOnlyWith) && item.showOnlyWith.length > 0) {
          // showOnlyWith is populated - only show if current simulator is in the list
          if (currentSimulatorId) {
            // showOnlyWith can be array of ID strings or objects with _id
            const isIncluded = item.showOnlyWith.some(simRef => {
              const simId = typeof simRef === 'string' ? simRef : (simRef._id || simRef);
              return simId === currentSimulatorId;
            });

            if (!isIncluded) {
              // Current simulator not in the list, skip this module
              return;
            }
          } else {
            // No simulator loaded (admin mode), skip modules with showOnlyWith restrictions
            return;
          }
        }
        // If showOnlyWith is empty or not set, module is shown for all simulators

        let mainCategory = 'Other';
        let subCategory = 'General';
        
        // Resolve masterCategory UUID to EXACT CMS name
        if (item.masterCategory && Array.isArray(item.masterCategory) && item.masterCategory.length > 0) {
          const categoryRef = item.masterCategory[0];
          const categoryId = categoryRef._id || categoryRef;
          const categoryData = categoryLookup[categoryId];
          if (categoryData) {
            mainCategory = categoryData.name;
          }
        }
        // FALLBACK: Use old category field as-is
        else if (item.category) {
          if (Array.isArray(item.category) && item.category.length > 0) {
            mainCategory = sanitizeInput(item.category[0]);
          } else if (typeof item.category === 'string') {
            try {
              const parsedCategory = JSON.parse(item.category);
              if (Array.isArray(parsedCategory) && parsedCategory.length > 0) {
                mainCategory = sanitizeInput(parsedCategory[0]);
              } else {
                mainCategory = sanitizeInput(item.category);
              }
            } catch (e) {
              mainCategory = sanitizeInput(item.category);
            }
          }
        }
        
        // Resolve masterSubCategory UUID to EXACT CMS name
        if (item.masterSubCategory && Array.isArray(item.masterSubCategory) && item.masterSubCategory.length > 0) {
          const subcategoryRef = item.masterSubCategory[0];
          const subcategoryId = subcategoryRef._id || subcategoryRef;
          const subcategoryData = subcategoryLookup[subcategoryId];
          if (subcategoryData) {
            subCategory = subcategoryData.name;
          }
        }
        // FALLBACK: Use old subCategory field as-is
        else if (item.subCategory) {
          if (Array.isArray(item.subCategory) && item.subCategory.length > 0) {
            subCategory = sanitizeInput(item.subCategory[0]);
          } else if (typeof item.subCategory === 'string') {
            try {
              const parsedSubCategory = JSON.parse(item.subCategory);
              if (Array.isArray(parsedSubCategory) && parsedSubCategory.length > 0) {
                subCategory = sanitizeInput(parsedSubCategory[0]);
              } else {
                subCategory = sanitizeInput(item.subCategory);
              }
            } catch (e) {
              subCategory = sanitizeInput(item.subCategory);
            }
          }
        }
        
        // Initialize category structure
        if (!groupedProducts[mainCategory]) {
          groupedProducts[mainCategory] = {};
        }
        if (!groupedProducts[mainCategory][subCategory]) {
          groupedProducts[mainCategory][subCategory] = [];
        }
        
        // Parse brand field - sanitize brand data
        let brand = item.brand || item.manufacturer || item.brandName || item.Brand || [];
        if (typeof brand === 'string') {
          brand = [sanitizeInput(brand)];
        } else if (Array.isArray(brand)) {
          brand = brand.map(b => sanitizeInput(b));
        }
        
        // Description is now HTML from backend (rich text converted server-side)
        // Store as 'description' for compatibility with UI components, render with dangerouslySetInnerHTML
        const descriptionHtml = item.description || '';
        
        // Create product object with EXACT CMS categories and sanitized data
        const product = {
          name: sanitizeInput(item.title || 'Untitled Product'),
          price: item.price || 0,
          testPrice: item.test_price || null,
          id: sanitizeInput(item._id || Math.random().toString(36)),
          mainCategory: mainCategory,
          subCategory: subCategory,
          description: descriptionHtml, // HTML from backend - render with dangerouslySetInnerHTML
          image: item.image || null,
          slug: sanitizeInput(item.slug || ''),
          style: Array.isArray(item.style) ? item.style.map(s => sanitizeInput(s)) : [],
          requires: Array.isArray(item.requires) ? item.requires.map(r => sanitizeInput(r)) : [],
          brand: brand,
          isLocked: isLockedProduct(null, mainCategory, subCategory),
          displayTypeCompatibility: item.displayTypeCompatibility || null,
          displaySize: item.displaySize || null,
          maxDisplaySize: item.maxDisplaySize || null,
          minDisplaySize: item.minDisplaySize || null
        };
        
        groupedProducts[mainCategory][subCategory].push(product);
      });
      
      // STEP 4: Enhance backend config with any missing categories from products
      // CRITICAL: We must preserve all existing config data including icons!
      const enhancedCategoryConfig = { ...backendCategoryConfig };
      
      // Add any categories from products that aren't in the backend config
      Object.keys(groupedProducts).forEach(mainCategory => {
        if (!enhancedCategoryConfig[mainCategory]) {
          // New category not in backend - create it
          const subcategories = Object.keys(groupedProducts[mainCategory]);
          const generalIndex = subcategories.findIndex(sub => sub === 'General');
          let orderedSubcategories;
          
          if (generalIndex !== -1) {
            // Put General first, then sort the rest
            const withoutGeneral = subcategories.filter(sub => sub !== 'General');
            orderedSubcategories = ['General', ...withoutGeneral.sort()];
          } else {
            orderedSubcategories = subcategories.sort();
          }
          
          enhancedCategoryConfig[mainCategory] = {
            categoryOrder: 999,
            subcategoryOrder: orderedSubcategories,
            icon: null
          };
        } else {
          // IMPORTANT: Preserve the existing config while updating subcategory order
          const existingConfig = enhancedCategoryConfig[mainCategory];
          const actualSubcategories = Object.keys(groupedProducts[mainCategory]);
          const configuredSubcategories = existingConfig.subcategoryOrder || [];
          
          // Check if General exists but isn't in the order
          const hasGeneral = actualSubcategories.includes('General');
          const generalInOrder = configuredSubcategories.includes('General');
          
          // Add any missing subcategories to the order
          const missingSubcategories = actualSubcategories.filter(sub => 
            !configuredSubcategories.includes(sub)
          );
          
          // Only update if necessary
          if (missingSubcategories.length > 0 || (hasGeneral && !generalInOrder)) {
            let newSubcategoryOrder;
            
            // If General needs to be added first
            if (hasGeneral && !generalInOrder) {
              const withoutGeneral = missingSubcategories.filter(sub => sub !== 'General');
              newSubcategoryOrder = [
                'General',
                ...configuredSubcategories,
                ...withoutGeneral.sort()
              ];
            } else {
              newSubcategoryOrder = [
                ...configuredSubcategories,
                ...missingSubcategories.sort()
              ];
            }
            
            // CRITICAL: Use spread to preserve all existing config including icon!
            enhancedCategoryConfig[mainCategory] = {
              ...existingConfig,
              subcategoryOrder: newSubcategoryOrder
            };
          }
        }
        
        // Add subcategory configs if they don't exist
        Object.keys(groupedProducts[mainCategory]).forEach(subCategory => {
          if (!enhancedCategoryConfig[subCategory] && !backendCategoryConfig[subCategory]) {
            // Don't store icon for "General" - it should be resolved dynamically
            enhancedCategoryConfig[subCategory] = {
              icon: null
            };
          }
        });
      });
      
      // STEP 5: Filter categories based on simulator type
      let filteredProducts = groupedProducts;

      if (data.baseConfig) {
        if (isFlightSimulator(data.baseConfig)) {
          filteredProducts = { ...groupedProducts };
          delete filteredProducts['Racing Controls'];
        } else {
          filteredProducts = { ...groupedProducts };
          delete filteredProducts['Flight Controls'];
        }
      }

      // STEP 5a: Apply CMS-defined category and module exclusions from baseConfig
      if (data.baseConfig) {
        // Get exclusion lists from baseConfig (multi-reference fields from CMS)
        const excludedMasterCategories = data.baseConfig.excludeMasterCategories || [];
        const excludedSubcategories = data.baseConfig.excludeSubcategories || [];
        const excludedModules = data.baseConfig.excludedModules || [];

        // Build sets for efficient lookup
        const excludedMasterSet = new Set();
        const excludedSubSet = new Set();
        const excludedModuleSet = new Set();

        // Process master category exclusions
        if (Array.isArray(excludedMasterCategories)) {
          excludedMasterCategories.forEach(catRef => {
            // Handle both reference objects and direct IDs
            const categoryId = catRef._id || catRef;
            const categoryData = categoryLookup[categoryId];
            if (categoryData) {
              excludedMasterSet.add(categoryData.name);
            }
          });
        }

        // Process subcategory exclusions
        if (Array.isArray(excludedSubcategories)) {
          excludedSubcategories.forEach(subRef => {
            // Handle both reference objects and direct IDs
            const subcategoryId = subRef._id || subRef;
            const subcategoryData = subcategoryLookup[subcategoryId];
            if (subcategoryData) {
              excludedSubSet.add(subcategoryData.name);
            }
          });
        }

        // Process individual module exclusions
        if (Array.isArray(excludedModules)) {
          excludedModules.forEach(modRef => {
            // Handle both reference objects and direct IDs
            const moduleId = modRef._id || modRef;
            excludedModuleSet.add(moduleId);
          });
        }

        // Apply exclusions
        if (excludedMasterSet.size > 0 || excludedSubSet.size > 0 || excludedModuleSet.size > 0) {
          const filteredCopy = {};

          Object.keys(filteredProducts).forEach(mainCategory => {
            // Skip if master category is excluded
            if (excludedMasterSet.has(mainCategory)) {
              return;
            }

            // Filter subcategories
            const subcategories = filteredProducts[mainCategory];
            const filteredSubcategories = {};

            Object.keys(subcategories).forEach(subCategory => {
              // Skip if subcategory is excluded
              if (excludedSubSet.has(subCategory)) {
                return;
              }

              // Filter individual products/modules
              const products = subcategories[subCategory];
              const filteredProducts = products.filter(product => {
                // Exclude if product ID matches any excluded module
                return !excludedModuleSet.has(product.id);
              });

              // Only include subcategory if it has remaining products
              if (filteredProducts.length > 0) {
                filteredSubcategories[subCategory] = filteredProducts;
              }
            });

            // Only include main category if it has remaining subcategories
            if (Object.keys(filteredSubcategories).length > 0) {
              filteredCopy[mainCategory] = filteredSubcategories;
            }
          });

          filteredProducts = filteredCopy;
        }
      }
      
      // STEP 6: Sort categories using the enhanced config
      const sortedMainCategories = sortCategoriesWithConfig(
        Object.keys(filteredProducts), 
        enhancedCategoryConfig
      );
      
      setCategoryConfig(enhancedCategoryConfig);
      setProductData(filteredProducts);
      setMainCategories(sortedMainCategories);
      
      return { 
        groupedProducts: filteredProducts, 
        baseConfig: data.baseConfig,
        categoryConfig: enhancedCategoryConfig 
      };
      
    } catch (err) {
      setError(err.message);
      
      // Fallback demo data
      const demoData = getDemoData();
      setProductData(demoData);
      setMainCategories(Object.keys(demoData));
      setCategoryConfig({});
      
      return { 
        groupedProducts: demoData, 
        baseConfig: null,
        categoryConfig: {} 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    productData, 
    mainCategories, 
    categoryConfig, 
    loading, 
    error, 
    fetchProducts 
  };
};